import React from 'react';
import { positionMenuByTrigger } from '../../utils/menuPosition';
import {useSelector, useDispatch} from 'react-redux';
import {ProgressBar, Button, Dropdown} from 'react-bootstrap';
import {setFiles, setProgress, setIsProcessing, resetFilePath, setCancelRequested, setAutoAnalyze, setActiveTab} from '../../redux/slices/appSlice';
import { useToast } from '../../contexts/ToastContext';
import { track } from '../../utils/analytics';
import { loadCloudPlaylistFiles } from '../../utils/cloudPlaylistTracks';
import { offerFixedCopiesCommit } from '../../utils/fixedCopiesCommit';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import { confirmLargeSet, countAudioFiles, SET_COST } from '../../utils/largeSetGuard';
import {clearLogs, addLogs, updateOrAddLogs, addLogsLineBreak, removeLogsForFile, setLogs, clearPendingLogs} from '../../redux/slices/logsSlice';
import {setTotalWavFiles, setFixedFiles, setTotalAudioFiles, setIncompatibleFilesFound, setDependsFilesFound, setCompatibleFilesFound, setConvertedFilesFound} from '../../redux/slices/logsSlice';
import {setActiveFilter, setUseStatusBadges, setListGrouping, setListSort, setFileTypeFilter,
    setFixIncompatibleFiles, setConvertAiffToWav, setConversionFormat, setBackupOriginalFiles} from '../../redux/slices/fixSettingsSlice'
import { useDesignV2 } from '../../utils/designV2';
import ToolHeader, { PopoverRow } from '../v2/ToolHeader';
import ToggleSwitch from '../ToggleSwitch';
import CustomSelect from '../CustomSelect';
import { FLAC_DEPENDS_SUMMARY } from '../../constants/flacCompatibility';
import { isFolderSource, groupRowsByFolder, sortTrackRowsByLabel, useDisplayLabel } from '../../utils/folderGrouping';
import { addTrack, toggleAutoPlay, setAutoPlay, setAudioList, resetAudioList } from '../../redux/slices/musicPlayerSlice';
import { addToNextUp } from '../../utils/queueAdd';
import {formatPath} from "../../utils/formatPath";
import LoudnessSettings from "../loudnessSettings";
import FixSettings from "../fixSettings";
import ProgressBarWithStop from "../ProgressBarWithStop.js";
import ToolRow from '../v2/ToolRow';
import LogStatusIcon from "../LogStatusIcon";
import {ReactComponent as AnalyzeFilesIcon} from '../../assets/icons/analyze-files.svg';
import {ReactComponent as FixFilesIcon} from '../../assets/icons/fix-files.svg';
import {ReactComponent as DirectoryIcon } from '../../assets/icons/select-folder.svg';
import {ReactComponent as IncompatibleFileIcon } from '../../assets/icons/incompatible-file.svg';
import {ReactComponent as FixedFileIcon } from '../../assets/icons/fixed-file.svg';
import {ReactComponent as AudioFileIcon } from '../../assets/icons/audio-file.svg';
import {ReactComponent as WavFileIcon } from '../../assets/icons/wav-file.svg';
import {ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import {ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import {ReactComponent as StopIcon } from '../../assets/icons/stop.svg';
import {ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import {ReactComponent as OpenIcon } from '../../assets/icons/open.svg';
import DependsInfo, { IncompatibleInfo } from '../StatInfo';
import { statDigits } from '../../utils/statDigits';
import StatCount from '../StatCount';
import RowActions from '../RowActions';
import BulkRowContextMenu from '../BulkRowContextMenu';
import AddToPlaylistModal from '../AddToPlaylistModal';
import useRowSelection from '../../hooks/useRowSelection';
import { parseTrackFromPath } from '../../utils/parseTrackFromPath';
import { waitRemaining } from '../../utils/minVisibleDelay';
import { useEnrichedTracks, getEnrichedParts } from '../../hooks/useEnrichedTracks';
import { usePlayerRowSync, localPathFromMusicSrc } from '../../hooks/usePlayerRowSync';
import { setLocalListContext } from '../../utils/localListContext';
import { resolveOrderedAudioFiles } from '../../utils/orderedAudioFiles';
import CoverPlayButton from '../CoverPlayButton';
import { batchProgressTicks } from '../../utils/progressBatcher';
import './SmartRecsTab.scss';

// ---- Row rendering (module level, memoized) ----
// One finished file used to re-render EVERY row: the list was built inline
// in the tab, so each tick rebuilt 200 labels, cover URLs and context menus.
// Each row is now a memo component keyed by file path; its props are only
// its own log object (Immer keeps untouched rows' identity across ticks),
// booleans derived for THIS row (selected / playing), the settings it reads,
// and a stable `api` whose methods read the tab's latest handlers through a
// ref — so a tick re-renders only the row that changed.

const getStatusIcon = () => <WavFileIcon className="status-icon" />;

const getStatusBadge = (status) => {
    const labels = {
        'compatible': 'Compatible',
        'incompatible': 'Incompatible',
        'fixed': 'Fixed',
        'converted': 'Converted',
        'depends': 'Depends',
        'converting': 'Converting…',
        'fixing': 'Fixing…',
        'error': 'Error',
        'file-not-found': 'Not Found'
    };
    const title = status === 'file-not-found' ? 'File not found at original path' : '';
    return <span className={`status-badge ${status}`} title={title}>{labels[status] || status}</span>;
};

const getStatusIndicator = (status, useStatusBadges) => {
    // Pending = the REAL badge element with a quiet pulse (both badge and
    // icon modes) — identical box, so the result lands with zero shift.
    if (status === 'pending') return <span className="status-badge pending-skeleton" title="Waiting to be analyzed">{' '}</span>;
    return useStatusBadges ? getStatusBadge(status) : getStatusIcon(status);
};

// Small muted badge showing the file's format (WAV/MP3/FLAC/…) — this tab is
// all about format, so seeing it per-row reinforces the format filter.
const getFileTypeBadge = (log) => {
    const path = log.displayPath || log.filePath || '';
    const m = path.match(/\.([a-z0-9]{1,5})$/i);
    if (!m) return null;
    const ext = m[1].toLowerCase();
    const cls = ['wav', 'mp3', 'flac'].includes(ext) ? ext : (ext === 'aiff' || ext === 'aif' ? 'aiff' : 'other');
    return <span className={`filetype-badge ${cls}`}>{m[1].toUpperCase()}</span>;
};

const isTrackLogRow = (log) => !!(log?.filePath && log.status !== 'summary' && log.status !== 'separator' && log.status !== 'folder-header');

// DESIGN V2 row: status dot, the name, the reason in faint text, the format
// as grey text right-aligned, and a quiet per-row Fix / Convert on hover.
const V2_DOT = { compatible: 'ok', incompatible: 'bad', error: 'bad', 'file-not-found': 'bad', depends: 'warn', fixed: 'info', converted: 'info', fixing: 'info', converting: 'info', pending: 'muted' };
const v2FormatOf = (log) => {
    const m = (log.displayPath || log.filePath || '').match(/\.([a-z0-9]{1,5})$/i);
    return m ? m[1].toUpperCase() : '';
};
function CompatRowV2({ log, enriched, selected, playing, ctxAt, rowAction, api }) {
    const audioPath = log.displayPath || log.filePath;
    const status = log.status || '';
    const reason = log.skippedExisting
        ? 'Fixed copy exists'
        : status === 'file-not-found' ? 'File not found at its original path'
            : status === 'fixing' ? 'Fixing…' : status === 'converting' ? 'Converting…'
                : (log.detail || '');
    return (
        <ToolRow
            rowKey={log.filePath}
            className="v2-ct-row"
            status={status}
            selected={selected}
            onClick={(e) => api.rowClick(log, e)}
            onContextMenu={(e) => api.rowContextMenu(log, e)}
            onDoubleClick={(e) => api.playClick(log, e)}
            cover={(
                <CoverPlayButton
                    filePath={audioPath}
                    cover={enriched.cover}
                    isPlaying={playing}
                    onClick={(e) => playing ? api.pauseClick(e) : api.playClick(log, e)}
                />
            )}
            dot={<span className={`v2-dot ${V2_DOT[status] || 'muted'}${status === 'pending' ? ' v2-pulse' : ''}`} title={status === 'pending' ? 'Waiting to be analyzed' : status} />}
            name={enriched.label}
            nameTitle={log.filePath}
            middle={<span className="v2-ct-reason" title={log.skippedExisting ? `${log.detail} — enable "Overwrite existing fixed files" in Settings to re-create it` : (reason || undefined)}>{reason}</span>}
            number={<span className="v2-ct-format">{v2FormatOf(log)}</span>}
            action={rowAction && (
                <button type="button" className="v2-btn quiet sm" onClick={(e) => { e.stopPropagation(); api.fixRow(log); }} title={rowAction === 'Convert' ? 'Convert this file now' : 'Fix this file now'}>{rowAction}</button>
            )}
            menu={(
                <RowActions
                    track={{ title: enriched.title, artist: enriched.artist, filePath: audioPath, artwork: enriched.cover || null, djpalId: enriched.djpalId || null }}
                    onAddToQueue={() => api.addToQueue(log, { stopPropagation() {} })}
                    onRemoveFromLibrary={() => api.removeFromList([log.filePath], 'menu')}
                    removeLabel="Remove from list"
                    openAtPoint={ctxAt}
                    desktopLike
                    analyticsFrom="fix_my_tracks"
                />
            )}
        />
    );
}

const CompatLogRow = React.memo(function CompatLogRow({ log, enrichedMap, selected, playing, ctxAt, useStatusBadges, api, v2 = false, rowAction = null }) {
    const isTrack = isTrackLogRow(log);
    const audioPath = log.displayPath || log.filePath;
    const enriched = isTrack ? getEnrichedParts(enrichedMap, audioPath) : null;
    if (v2 && isTrack) {
        return <CompatRowV2 log={log} enriched={enriched} selected={selected} playing={playing} ctxAt={ctxAt} rowAction={rowAction} api={api} />;
    }
    return (
        <div
            data-rowkey={isTrack ? log.filePath : undefined}
            className={`log-entry ${log && log.status ? log.status : ''} ${isTrack && selected ? 'selected-row' : ''}`}
            onClick={isTrack ? (e) => api.rowClick(log, e) : undefined}
            // Shift-click extends the selection — suppress the browser's
            // native shift-click text-selection so the two don't fight.
            onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
            onContextMenu={(e) => api.rowContextMenu(log, e)}
            onDoubleClick={(e) => api.playClick(log, e)}
        >
            {isTrack && (
                <CoverPlayButton
                    filePath={audioPath}
                    cover={enriched.cover}
                    isPlaying={playing}
                    onClick={(e) => playing ? api.pauseClick(e) : api.playClick(log, e)}
                />
            )}
            {log.status && log.status !== 'summary' && log.status !== 'separator' && getStatusIndicator(log.status, useStatusBadges)}
            {isTrack && getFileTypeBadge(log)}
            <div className="log-text">
                {isTrack ? (
                    // Track row: show the enriched "Artist - Title" we already know
                    // (Mixpedia/ID3), falling back to the filename.
                    <div title={log.skippedExisting ? undefined : (log.detail || undefined)}>
                        {enriched.label}
                        {log.skippedExisting
                            ? <span className="log-note-pill" title={`${log.detail} — enable "Overwrite existing fixed files" in Settings to re-create it`}>Fixed copy exists</span>
                            : log.detail && <span className="error-detail"> — {log.detail}</span>}
                    </div>
                ) : (
                    (log && log.msg || "").split('\n').map((line, i) => (
                        <div key={i}
                             className={i === 0 && line && line.startsWith("You've reached the") || line.startsWith("Go premium") ? "limit-reached" : ""}>
                            {i === 0 && <LogStatusIcon icon={log.icon} />}{line}
                        </div>
                    ))
                )}
            </div>
            {isTrack && (
                <RowActions
                    track={{ title: enriched.title, artist: enriched.artist, filePath: audioPath, artwork: enriched.cover || null, djpalId: enriched.djpalId || null }}
                    onAddToQueue={() => api.addToQueue(log, { stopPropagation() {} })}
                    onRemoveFromLibrary={() => api.removeFromList([log.filePath], 'menu')}
                    removeLabel="Remove from list"
                    openAtPoint={ctxAt}
                    desktopLike
                    analyticsFrom="fix_my_tracks"
                />
            )}
        </div>
    );
});

const FixMyTracksTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    // Real title/artist/cover for local files we've already analyzed, so rows
    // and the player show "CLEAR - Don't Do It" instead of the raw filename.
    const enrichedMap = useEnrichedTracks();

    // Local state for playback control
    const [currentlyPlayingPath, setCurrentlyPlayingPath] = React.useState(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    // Reconcile the row highlight with the bottom player: pause/stop flips the
    // icon back, and list auto-advance moves the highlight to the next row.
    usePlayerRowSync(currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath);

    // Right-click menus: single-row opens the row's shared TrackContextMenu at
    // the cursor (identical to its ⋯ menu); a right-click inside a multi-row
    // selection opens the bulk menu.
    const [rowCtx, setRowCtx] = React.useState(null);   // { key, x, y, nonce }
    const [bulkCtx, setBulkCtx] = React.useState(null); // { x, y }
    const ctxNonceRef = React.useRef(0);
    // Bulk "Add N Tracks to Playlist" picker targets.
    const [bulkPlaylistTargets, setBulkPlaylistTargets] = React.useState([]);

    // Drag and drop state
    const [isDragging, setIsDragging] = React.useState(false);

    // Source identity guard (same as MatchLoudnessTab): playlist selection
    // resolves ASYNCHRONOUSLY, and an Analyze click racing it used to run the
    // PREVIOUS source while the header already showed the playlist.
    const sourceEpochRef = React.useRef(0);
    const [sourceResolving, setSourceResolving] = React.useState(false);

    // Playlist dropdown state
    const [selectedPlaylistName, setSelectedPlaylistName] = React.useState(null);
    // Identity of the source playlist ({source: 'cloud'|'local', id,
    // rowIdsByPath}) — lets a keep-originals Fix swap produced files into the
    // playlist afterwards. null for folder/files/My Library sources.
    const [selectedPlaylistRef, setSelectedPlaylistRef] = React.useState(null);
    const [playlistDropdownOpen, setPlaylistDropdownOpen] = React.useState(false);
    const [playlistList, setPlaylistList] = React.useState([]);
    const [playlistsLoading, setPlaylistsLoading] = React.useState(false);
    const [playlistSearch, setPlaylistSearch] = React.useState('');
    const [playlistDropdownPos, setPlaylistDropdownPos] = React.useState({ top: 0, left: 0, width: 250 });
    const playlistDropdownRef = React.useRef(null);
    const playlistBtnRef = React.useRef(null);
    // Flip the "Open ▸ Playlist" picker above its button when there's no room below.
    React.useLayoutEffect(() => {
        if (!playlistDropdownOpen || !playlistDropdownRef.current || !playlistBtnRef.current) return;
        const rect = playlistBtnRef.current.getBoundingClientRect();
        const { top } = positionMenuByTrigger(playlistDropdownRef.current, rect, { gap: 2, align: null });
        setPlaylistDropdownPos(prev => (prev.top === top ? prev : { ...prev, top }));
    }, [playlistDropdownOpen, playlistList, playlistSearch, playlistsLoading]);
    const playlistSearchRef = React.useRef(null);

    // Selectors
    const files = useSelector((state) => state.app.files);
    const logs = useSelector((state) => state.logs.logs);
    // DESIGN V2 — structural changes (tool header, chip filters, dot rows)
    // are gated on this; the old structure is untouched while it is off.
    const v2 = useDesignV2();
    const v2AutoArmedRef = React.useRef(null);  // DESIGN V2: one auto-analyze per loaded source
    const isProcessing = useSelector((state) => state.app.isProcessing);
    const progress = useSelector((state) => state.app.progress);
    const cancelRequested = useSelector((state) => state.app.cancelRequested);
    const autoAnalyze = useSelector((state) => state.app.autoAnalyze);
    // Signed-in users' playlists live in the cloud — the picker lists those
    // and resolves their tracks to local files (utils/cloudPlaylistTracks).
    const accessToken = useSelector((state) => state.auth.accessToken);
    const useServer = !!accessToken;
    const totalAudioFiles = useSelector((state) => state.logs.totalAudioFiles);
    const totalWavFiles = useSelector((state) => state.logs.totalWavFiles);
    const incompatibleFilesFound = useSelector((state) => state.logs.incompatibleFilesFound);
    const fixedFiles = useSelector((state) => state.logs.fixedFiles);
    const dependsFilesFound = useSelector((state) => state.logs.dependsFilesFound);
    const compatibleFilesFound = useSelector((state) => state.logs.compatibleFilesFound);
    const convertedFilesFound = useSelector((state) => state.logs.convertedFilesFound);

    const {
        enableRecursive,
        overwriteExistingFixedFiles,
        concurrencyLevel,
    } = useSelector((state) => state.settings);

    // Any change to the source value bumps the epoch — including dispatches
    // from OUTSIDE this tab (playlist sidebar "Open in tool"), which the
    // per-handler bumps can't see.
    const sourceFiles_watch = React.useRef(files);
    React.useEffect(() => {
        if (sourceFiles_watch.current !== files) {
            sourceFiles_watch.current = files;
            sourceEpochRef.current += 1;
        }
    }, [files]);

    const {
        showOnlyFixes,
        backupOriginalFiles,
        fixIncompatibleFiles,
        convertAiffToWav,
        conversionFormat,
        activeFilters,
        activeFileTypes,
        useStatusBadges,
        listGrouping,
        listSort
    } = useSelector((state) => state.fixSettings);

    // What the Fix pass can still do under the current settings: patch
    // incompatible WAV headers and/or convert FLACs. Derived from the log ROWS,
    // not the run counters — counters describe the last run, rows describe the
    // list's current state. Same derive-from-rows pattern as MatchLoudnessTab.
    // The produced set always comes from ALL logs: an original whose fixed
    // copy exists is "done" even when the copy's row is filtered out of view.
    const countFixable = React.useCallback((rows, allRows) => {
        // Originals a produced file points back to ([fixed] copy / conversion
        // output carry oldFilePath) — those are done, not pending.
        const produced = new Set();
        for (const log of allRows) if (log?.oldFilePath) produced.add(log.oldFilePath);
        const seen = new Set();
        let count = 0;
        for (const log of rows) {
            if (!log?.filePath || seen.has(log.filePath) || produced.has(log.filePath)) continue;
            seen.add(log.filePath);
            if (fixIncompatibleFiles && log.status === 'incompatible') count++;
            else if (convertAiffToWav && log.status === 'depends' && /\.flac$/i.test(log.filePath)) count++;
        }
        return count;
    }, [fixIncompatibleFiles, convertAiffToWav]);
    const fixableCount = React.useMemo(() => countFixable(logs, logs), [logs, countFixable]);

    // The log list as rendered (status + file-type filters applied) — moved
    // out of the JSX so row selection can share the same ordering. This IS
    // the Fix run's scope: what you see is what runs. Cmd+F find is
    // highlight-only and deliberately never narrows this list.
    const filteredLogs = React.useMemo(() => logs.filter((log) => {
        if (log?.status === 'summary') return false;
        const showOnlyFixesFilter = !showOnlyFixes || (log?.status && log.status !== 'compatible');

        // Apply file type filters (multi-select with OR logic)
        let fileTypeFilter = true;
        if (activeFileTypes.length > 0 && log?.filePath) {
            const filePath = log.filePath.toLowerCase();
            const isWav = filePath.endsWith('.wav');
            const isMp3 = filePath.endsWith('.mp3');
            const isAiff = filePath.endsWith('.aiff') || filePath.endsWith('.aif');
            const isFlac = filePath.endsWith('.flac');
            const isOther = !isWav && !isMp3 && !isAiff && !isFlac;

            fileTypeFilter = (
                (activeFileTypes.includes('wav') && isWav) ||
                (activeFileTypes.includes('mp3') && isMp3) ||
                (activeFileTypes.includes('aiff') && isAiff) ||
                (activeFileTypes.includes('flac') && isFlac) ||
                (activeFileTypes.includes('others') && isOther)
            );
        }

        // Apply active filters (multi-select with OR logic). A corrupted /
        // unreadable file is logged as 'error' but counted under Incompatible
        // (it won't play anywhere either), so the Incompatible chip must show
        // it — otherwise the counter says 1 and the filtered list says none.
        let statusFilter = true;
        if (activeFilters.length > 0) {
            const bucket = log?.status === 'error' ? 'incompatible' : log?.status;
            statusFilter = activeFilters.includes(bucket);
        }
        // Empty array shows everything

        return showOnlyFixesFilter && statusFilter && fileTypeFilter;
    }), [logs, showOnlyFixes, activeFileTypes, activeFilters]);

    // Visible-scope counts: what the Fix button will actually act on, and how
    // much actionable work the active filters are hiding (drives the hint line
    // and the pre-run confirm).
    const visibleFixableCount = React.useMemo(
        () => countFixable(filteredLogs, logs),
        [filteredLogs, logs, countFixable]
    );
    const hiddenFixableCount = fixableCount - visibleFixableCount;
    // Slot width for every count in the stats row: the run's total is the
    // ceiling for all of them, so the row is laid out once per run.
    const countDigits = statDigits(totalAudioFiles, compatibleFilesFound, incompatibleFilesFound, dependsFilesFound, fixedFiles, convertedFilesFound, hiddenFixableCount);
    // Row-derived "has this list been analyzed" — the totalAudioFiles counter
    // describes the last RUN, not the list (it's 0 for a restored session).
    const hasTrackRows = React.useMemo(
        () => logs.some((l) => l?.filePath && l.status !== 'summary'),
        [logs]
    );

    // ---- Multi-row selection (Finder semantics, shared hook) ----
    // Only real track rows are selectable — summary/separator/message lines
    // have no filePath and stay inert.
    const isTrackLog = (log) => !!(log?.filePath && log.status !== 'summary' && log.status !== 'separator' && log.status !== 'folder-header');

    // Name/Folders view toggle — folder sources only (playlists/library/file
    // picks carry their own order). 'folder' groups the visible rows under
    // subfolder headers at RENDER time; the underlying logs stay flat so
    // in-place result updates are unaffected.
    const folderSource = isFolderSource(files);
    const labelFor = useDisplayLabel(enrichedMap);
    const displayRows = React.useMemo(() => {
        const trackPathOf = (row) => (isTrackLog(row) ? (row.displayPath || row.filePath) : null);
        if (folderSource) {
            if (listGrouping !== 'folder') return filteredLogs; // already label-ordered by pre-populate
            return groupRowsByFolder(filteredLogs, files[0], trackPathOf, labelFor);
        }
        // Playlist/library sources: 'added' keeps the source's own order;
        // 'name' re-sorts at render. Picked files/drops have no toggle and
        // always keep pick order.
        if (!selectedPlaylistName || listSort !== 'name') return filteredLogs;
        return sortTrackRowsByLabel(filteredLogs, trackPathOf, labelFor);
    }, [filteredLogs, folderSource, listGrouping, listSort, files, labelFor, selectedPlaylistName]);

    const rowKeys = React.useMemo(() => {
        const seen = new Set();
        const keys = [];
        for (const log of displayRows) {
            if (isTrackLog(log) && !seen.has(log.filePath)) { seen.add(log.filePath); keys.push(log.filePath); }
        }
        return keys;
    }, [displayRows]);
    const keyIndex = React.useMemo(() => new Map(rowKeys.map((k, i) => [k, i])), [rowKeys]);

    // The visible rows as PLAYABLE paths (fixed copies play via displayPath),
    // in list order — this becomes the player's local list context so a track
    // ending advances to the next row. Missing files stay out: advancing into
    // one would 404 and bounce the player around.
    const playableRowPaths = React.useMemo(() => {
        const seen = new Set();
        const paths = [];
        for (const log of displayRows) {
            if (!isTrackLog(log) || log.status === 'file-not-found') continue;
            const p = log.displayPath || log.filePath;
            if (p && !seen.has(p)) { seen.add(p); paths.push(p); }
        }
        return paths;
    }, [displayRows]);

    // If the visible order changes while one of THIS list's tracks is playing
    // (sort/grouping toggle, filters, rows still streaming in), re-register
    // the play-through context so "next" follows the CURRENT order — not the
    // order at the moment play was clicked.
    const orderKeyRef = React.useRef('');
    React.useEffect(() => {
        const key = playableRowPaths.join('\n');
        const prev = orderKeyRef.current;
        orderKeyRef.current = key;
        if (!currentlyPlayingPath || !prev || prev === key) return;
        // Anchor on the PLAYER's actual track when possible — the tab
        // highlight follows the player asynchronously, and re-registering on
        // a stale highlight right after an auto-advance would replay a row.
        const playerPath = localPathFromMusicSrc(window.musicPlayerInstance?.src);
        const anchor = (playerPath && playableRowPaths.includes(playerPath)) ? playerPath : currentlyPlayingPath;
        if (!playableRowPaths.includes(anchor)) return;
        setLocalListContext(dispatch, playableRowPaths, anchor, enrichedMap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playableRowPaths]);

    // Remove rows from the SESSION list only (log entries + the pending file
    // selection) — nothing is deleted from the local DB, so no undo toast.
    const removeFilesFromList = React.useCallback((paths, via) => {
        if (!paths?.length) return;
        const pathSet = new Set(paths);
        paths.forEach((p) => dispatch(removeLogsForFile(p)));
        dispatch(setFiles(files.filter((f) => !pathSet.has(f))));
        track('tracks_removed_from_list', { tab: 'fix_my_tracks', count: paths.length, via });
    }, [dispatch, files]);

    const listContainerRef = React.useRef(null);
    const scrollRowIntoView = React.useCallback((index, key) => {
        listContainerRef.current
            ?.querySelector(`[data-rowkey="${CSS.escape(key)}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, []);
    const onDeleteSelected = React.useCallback((paths) => removeFilesFromList(paths, 'delete_key'), [removeFilesFromList]);
    const {
        selected: selectedPaths,
        orderedSelected,
        handleRowClick,
        selectOnly,
    } = useRowSelection({ keys: rowKeys, onDelete: onDeleteSelected, onArrowFocus: scrollRowIntoView, rowSelector: '.log-entry' });

    const handleRowContextMenu = (log, e) => {
        if (!isTrackLog(log)) return;
        e.preventDefault();
        const key = log.filePath;
        if (selectedPaths.size > 1 && selectedPaths.has(key)) {
            setBulkCtx({ x: e.clientX, y: e.clientY });
            return;
        }
        selectOnly(key, keyIndex.get(key));
        setRowCtx({ key, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    // Handlers
    const handleChooseFiles = async () => {
        setSelectedPlaylistName(null);
        setSelectedPlaylistRef(null);
        const chosenFiles = await window.electron.openFile(['wav']);
        sourceEpochRef.current += 1;
        if (chosenFiles?.length) dispatch(clearLogs());
        dispatch(setFiles(chosenFiles || []));
    };

    const handleChooseDir = async () => {
        const chosenDir = await window.electron.openDir();
        if (!chosenDir || chosenDir.length === 0) return; // dialog canceled — keep current selection
        setSelectedPlaylistName(null);
        setSelectedPlaylistRef(null);
        sourceEpochRef.current += 1;
        dispatch(clearLogs());
        dispatch(setFiles(chosenDir));
    };

    const handleChooseLibrary = async () => {
        try {
            const allTracks = await window.electron.db.tracks.getAll();
            const filePaths = (allTracks || [])
                .map(t => t.file_path || t.filePath || t.file)
                .filter(f => f && !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
            if (filePaths.length === 0) {
                toast.warning('Your library is empty'); return;
            }
            setSelectedPlaylistName('My Library');
            setSelectedPlaylistRef(null);
            sourceEpochRef.current += 1;
            dispatch(clearLogs());
            dispatch(setFiles(filePaths));
        } catch (error) {
            console.error('Error loading library:', error);
            toast.error('Failed to load library');
        }
    };

    const handleResetFilePath = () => {
        setSelectedPlaylistName(null);
        setSelectedPlaylistRef(null);
        sourceEpochRef.current += 1;
        dispatch(resetFilePath());
        dispatch(clearLogs());
        dispatch(setActiveFilter('all'));
        dispatch(setTotalAudioFiles(0));
        dispatch(setTotalWavFiles(0));
        dispatch(setIncompatibleFilesFound(0));
        dispatch(setFixedFiles(0));
        dispatch(setDependsFilesFound(0));
        dispatch(setCompatibleFilesFound(0));
        dispatch(setConvertedFilesFound(0));
    }

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files).map(file => window.electron.getPathForFile(file)).filter(Boolean);
        if (droppedFiles.length > 0) {
            // Dropped files replace the selection — the previous playlist (if
            // any) is no longer the source, so its label and repoint target go.
            setSelectedPlaylistName(null);
            setSelectedPlaylistRef(null);
            sourceEpochRef.current += 1;
            dispatch(clearLogs());
            dispatch(setFiles(droppedFiles));
        }
    };

    const handleRefresh = () => {
        handleRunYourMagic(false); // Run analyze again
    }

    const handleStopProcessing = () => {
        dispatch(setCancelRequested(true));
        dispatch(setIsProcessing(false));
        dispatch(clearPendingLogs()); // Cancelled rows must not linger as "Queued"
        window.electron.cancelFixing(); // Implemented in backend
        // dispatch(addLogs([{ msg: '⛔ Processing cancelled', status: 'error' }]));
    };

    // Latest handlers for the memoized rows (see CompatLogRow). The row's
    // `api` prop never changes identity; each method reads the current
    // closure through this ref, so rows don't re-render on every tab render.
    const rowHandlersRef = React.useRef({});
    const rowApi = React.useMemo(() => ({
        rowClick: (log, e) => rowHandlersRef.current.rowClick(log, e),
        rowContextMenu: (log, e) => rowHandlersRef.current.rowContextMenu(log, e),
        playClick: (log, e) => rowHandlersRef.current.playClick(log, e),
        pauseClick: (e) => rowHandlersRef.current.pauseClick(e),
        addToQueue: (log, e) => rowHandlersRef.current.addToQueue(log, e),
        removeFromList: (paths, via) => rowHandlersRef.current.removeFromList(paths, via),
        fixRow: (log) => rowHandlersRef.current.fixRow(log),
    }), []);

    const handlePlayClick = async (log, e) => {
        e.stopPropagation();

        // Only handle play for actual track entries (not summary lines)
        if (!log.filePath || log.status === 'summary') {
            return;
        }

        try {
            const audioPath = log.displayPath || log.filePath;

            // If clicking on currently playing track that's paused, resume it
            if (currentlyPlayingPath === audioPath && !isPlaying) {
                if (window.musicPlayerInstance) {
                    window.musicPlayerInstance.play();
                }
                // Re-assert the list context: it may have been cleared (radio
                // hand-off, another surface) while this row stayed current —
                // resuming must restore play-through-the-list, not solo play.
                setLocalListContext(dispatch, playableRowPaths, audioPath, enrichedMap);
                setIsPlaying(true);
                dispatch(setAutoPlay(true));
                return;
            }

            // Prefer enriched (Mixpedia/ID3) title/artist/cover over the filename.
            const { title: trackName, artist, cover: enrichedCover } = getEnrichedParts(enrichedMap, audioPath);

            // Load and play new track - clear previous tracks first
            const musicSrc = await window.electron.getAudioUrl(audioPath);

            // Fall back to embedded album art only when we have no enriched cover.
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(audioPath);
            } catch (err) {
            }

            const newTrack = {
                name: trackName,
                singer: artist,
                cover: enrichedCover || albumArtUrl,
                musicSrc
            };

            // Clear playlist and play new track
            dispatch(setAudioList([newTrack]));
            // Register the visible rows as the player's list context so
            // playback continues down the list when this track ends —
            // auto-radio only takes over after the LAST row (same behavior
            // as the Library tab).
            setLocalListContext(dispatch, playableRowPaths, audioPath, enrichedMap);
            dispatch(setAutoPlay(true));

            setCurrentlyPlayingPath(audioPath);
            setIsPlaying(true);
        } catch (error) {
            console.error('Error playing track:', error);
        }
    };

    const handlePauseClick = (e) => {
        e.stopPropagation();
        // Use the audio instance directly to pause
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
        }
        setIsPlaying(false);
        dispatch(setAutoPlay(false));
    };

    const handleStopClick = (e) => {
        e.stopPropagation();
        // Use the audio instance directly to pause first
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
            window.musicPlayerInstance.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentlyPlayingPath(null);
        dispatch(setAutoPlay(false));
        dispatch(resetAudioList());
    };

    const handleAddToQueue = async (log, e) => {
        e.stopPropagation();

        // Only handle for actual track entries
        if (!log.filePath || log.status === 'summary') {
            return;
        }

        try {
            const audioPath = log.displayPath || log.filePath;

            // Load audio URL
            const musicSrc = await window.electron.getAudioUrl(audioPath);

            // Fall back to embedded album art only when we have no enriched cover.
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(audioPath);
            } catch (err) {
            }

            // Prefer enriched (Mixpedia/ID3) title/artist/cover over the filename.
            const { title: trackName, artist, cover: enrichedCover } = getEnrichedParts(enrichedMap, audioPath);

            const newTrack = {
                name: trackName,
                singer: artist,
                cover: enrichedCover || albumArtUrl,
                musicSrc
            };

            // Add to queue without clearing
            addToNextUp(dispatch, newTrack);
        } catch (error) {
            console.error('Error adding track to queue:', error);
        }
    };

    // `onlyFiles` (v2 per-row Fix / Convert): run the fix on just those
    // files instead of the visible rows.
    const handleRunYourMagic = async (fix, onlyFiles = null) => {
        // Source captured at click time; abort if it changes during any await
        // below (see sourceEpochRef).
        const runEpoch = sourceEpochRef.current;
        const sourceChanged = () => sourceEpochRef.current !== runEpoch;
        // Fix acts on the VISIBLE rows — what you see is what runs (the
        // status/file-type chips scope it). Analyze always re-scans the full
        // source, and a Fix with no analyzed rows yet falls back to the raw
        // source (old behavior).
        const scopedFix = fix && hasTrackRows;
        const runFiles = onlyFiles || (scopedFix ? rowKeys : files);
        if (scopedFix && runFiles.length === 0) return; // filters hide everything; button is disabled too

        // Filters are hiding work the run could have done — make skipping it
        // an explicit choice, with the counts spelled out. No prompt when the
        // view covers every fixable file (then filtered == unfiltered).
        if (scopedFix && !onlyFiles && hiddenFixableCount > 0) {
            const proceed = await toast.confirm(
                `Filters are on — Fix will run on the ${visibleFixableCount} fixable track${visibleFixableCount === 1 ? '' : 's'} in view and skip ${hiddenFixableCount} hidden one${hiddenFixableCount === 1 ? '' : 's'}. Clear the filters to fix all ${fixableCount}.`,
                'Filters are active',
                { confirmText: visibleFixableCount === 1 ? 'Fix 1 track' : `Fix ${visibleFixableCount} tracks`, confirmVariant: 'primary' }
            );
            if (!proceed) return;
        }

        // Time-based large-selection gate (silent for small sets) — applies to
        // library, playlist, folder and dropped files alike.
        const fileCount = await countAudioFiles(runFiles);
        const sourceLabel = selectedPlaylistName === 'My Library' ? 'library'
            : selectedPlaylistName ? 'playlist'
            : (files.length === 1 && fileCount !== 1) ? 'folder' : 'selection';
        if (!(await confirmLargeSet(toast, fileCount, {
            secondsPerFile: SET_COST.fixTracks,
            parallelism: Math.max(1, concurrencyLevel || 1),
            sourceLabel,
            tab: 'fix_my_tracks',
        }))) return;
        if (sourceChanged()) return;

        window.electron.analytics.track('compatibility_run', {
            mode: fix ? 'fix' : 'analyze',
            ...(scopedFix ? { visible_track_count: runFiles.length, hidden_fixable_count: hiddenFixableCount } : {}),
            ...(onlyFiles ? { single_row: true } : {}),
        });
        // Mark processing BEFORE the pre-populate walk: the walk can take
        // seconds on big trees, and with the flag unset a second Analyze
        // click (or a stale run's guard) could interleave with this one.
        dispatch(setIsProcessing(true));
        dispatch(setProgress(0));
        // Analyze rebuilds the list; Fix updates the analyzed rows IN PLACE
        // (produced files replace their original's row via oldFilePath) — the
        // list must not blank out and re-order under the user.
        if (!fix) {
            dispatch(clearLogs());
            // Pre-populate every row in SOURCE order (playlist position /
            // library sort / dialog selection; folders sort by the DISPLAYED
            // "Artist - Title" label) so the list never reorders as results
            // stream in — each result updates its "pending" row in place
            // (updateOrAddLogs), and whatever the run never reports on is
            // dropped at completion (clearPendingLogs).
            try {
                const ordered = await resolveOrderedAudioFiles(runFiles, {
                    includeSubDirs: enableRecursive,
                    labelFor: (p) => getEnrichedParts(enrichedMap, p).label,
                });
                if (sourceChanged()) { dispatch(setIsProcessing(false)); return; }
                if (ordered.length > 0) {
                    dispatch(setLogs(ordered.map((f) => ({
                        filePath: f,
                        msg: f.split(/[\\/]/).pop(),
                        status: 'pending',
                    }))));
                }
            } catch { /* enumeration failed — rows just append as they complete */ }
        }

        const startTime = Date.now(); // Record the start time

        // Fix produces new files (FLAC → converted, incompatible → "[fixed]"
        // copy). Repoint the session list old → new at the end of the run so
        // Refresh re-analyzes the outcome, not the original.
        const producedPaths = {};
        // The backend reports 100% twice (final logs + summary) — run the
        // completion work once.
        let finished = false;

        // This function will update progress based on backend reporting
        const updateProgressFromBackend = async ({progressPercentage, logs, fixedFiles, incompatibleFilesFound, dependsFilesFound,
                                                 compatibleFilesFound, convertedFilesFound, totalAudioFiles, totalWavFiles, notice, pendingDjUpdates, skippedExistingFixed}) => {
            // DJ-library sync notice (e.g. "Rekordbox is open — close it and re-run").
            if (notice) toast.warning(notice, 9000);
            if (fix && logs) {
                for (const log of logs) {
                    if (log.oldFilePath && log.filePath && log.oldFilePath !== log.filePath) {
                        producedPaths[log.oldFilePath] = log.filePath;
                    }
                }
            }
            // Apply this batch's rows BEFORE the completion branch: the final
            // file's log arrives in the SAME callback that reports 100%, and
            // clearPendingLogs below must not drop its still-pending row (that
            // re-added the last track at the top of the list).
            dispatch(updateOrAddLogs(logs));
            if (progressPercentage >= 100) {
                if (finished) return;
                finished = true;
                await waitRemaining(startTime); // Keep progress visible briefly so fast runs don't flash-complete (see minVisibleDelay)
                dispatch(setProgress(progressPercentage));
                dispatch(setIsProcessing(false));  // Processing is done
                dispatch(clearPendingLogs());      // Rows the walker never reached
                if (backupOriginalFiles) {
                    // Keep-originals: the copies exist but NOTHING switches to
                    // them (DJ libraries, source playlist, this list) until
                    // the user clicks "Use fixed files" — sticky toast shared
                    // with Match Loudness (fixedCopiesCommit.js).
                    if (pendingDjUpdates?.length) {
                        offerFixedCopiesCommit({
                            toast,
                            source: 'compat',
                            pendingDjUpdates,
                            noun: 'fixed',
                            playlistRef: selectedPlaylistRef,
                            playlistName: selectedPlaylistRef ? selectedPlaylistName : null,
                            accessToken,
                            repointSession: (byOld) => dispatch(setFiles(files.map((f) => byOld[f] || f))),
                        });
                    }
                } else if (Object.keys(producedPaths).length > 0) {
                    // In-place runs are auto-committed (old path is gone) —
                    // follow the produced files so Refresh re-analyzes the outcome.
                    dispatch(setFiles(files.map((f) => producedPaths[f] || f)));
                }
                // Keep-originals + "Overwrite existing fixed files" off: rows
                // whose [fixed] copy already exists stay "Incompatible" — say
                // why, or the run reads as a silent failure (same as Loudness).
                if (fix && backupOriginalFiles && skippedExistingFixed > 0) {
                    toast.info(`${skippedExistingFixed} file${skippedExistingFixed > 1 ? 's' : ''} skipped — a [fixed] copy already exists (enable "Overwrite existing fixed files" in Settings to re-create ${skippedExistingFixed > 1 ? 'them' : 'it'})`, 12000);
                }
                track('compatibility_run_complete', { success: true, track_count: files.length, duration_ms: Date.now() - startTime });
            } else {
                dispatch(setProgress(progressPercentage));
            }

            // Default missing counters to 0 — a partial backend payload must
            // never put undefined in the store (the Compatible stat is a
            // subtraction over these and renders NaN).
            dispatch(setIncompatibleFilesFound(incompatibleFilesFound ?? 0));
            dispatch(setFixedFiles(fixedFiles ?? 0));
            dispatch(setDependsFilesFound(dependsFilesFound ?? 0));
            dispatch(setCompatibleFilesFound(compatibleFilesFound ?? 0));
            dispatch(setConvertedFilesFound(convertedFilesFound ?? 0));
            dispatch(setTotalAudioFiles(totalAudioFiles ?? 0));
            dispatch(setTotalWavFiles(totalWavFiles ?? 0));
        };

        if (sourceChanged()) { dispatch(setIsProcessing(false)); return; }
        // Call the backend function, passing in the directory and progress callback
        window.electron.fixAllFilesInDir(
            runFiles,
            // Per-file ticks are folded into a few applies per second (see
            // utils/progressBatcher.js); the final tick flushes then applies.
            batchProgressTicks(updateProgressFromBackend),
            enableRecursive,
            backupOriginalFiles,
            concurrencyLevel,
            false, // showOnlyFixes,
            fix,
            overwriteExistingFixedFiles,
            convertAiffToWav,
            conversionFormat,
            fixIncompatibleFiles
        );
    };

    // Auto-analyze when triggered from playlist sidebar
    React.useEffect(() => {
        // DESIGN V2: a loaded source analyzes on its own; the button is the write step.
        // Arm once per load: a new files array, or the same array loaded again
        // after its results were cleared (not during a run, which clears too).
        if (v2 && files.length > 0 && logs.length === 0 && !isProcessing && !autoAnalyze
            && (v2AutoArmedRef.current?.files !== files || Date.now() - v2AutoArmedRef.current.at > 3000)) {
            v2AutoArmedRef.current = { files: files, at: Date.now() };
            dispatch(setAutoAnalyze(true));
            return;
        }
        if (autoAnalyze && files.length > 0 && !isProcessing) {
            dispatch(setAutoAnalyze(false)); // Reset the flag
            handleRunYourMagic(false); // Trigger analyze
        }
    }, [autoAnalyze, files, logs.length, isProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close playlist dropdown on outside click
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (playlistDropdownRef.current && !playlistDropdownRef.current.contains(e.target)) {
                setPlaylistDropdownOpen(false);
            }
        };
        if (playlistDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [playlistDropdownOpen]);

    const handlePlaylistDropdownToggle = async (anchorEl) => {
        if (playlistDropdownOpen) {
            setPlaylistDropdownOpen(false);
            return;
        }
        setPlaylistSearch('');
        if (anchorEl) {
            const rect = anchorEl.getBoundingClientRect();
            setPlaylistDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 250) });
        }
        setPlaylistDropdownOpen(true);
        setTimeout(() => playlistSearchRef.current?.focus(), 50);
        setPlaylistsLoading(true);
        try {
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                setPlaylistList(result?.data || []);
            } else {
                const allPlaylists = await window.electron.db.playlists.getAll(false);
                setPlaylistList(allPlaylists || []);
            }
        } catch (error) {
            console.error('Error loading playlists:', error);
            toast.error('Failed to load playlists');
        } finally {
            setPlaylistsLoading(false);
        }
    };

    const handlePlaylistSelect = async (playlist) => {
        setPlaylistDropdownOpen(false);
        setSourceResolving(true);
        try {
            let filePaths = [];
            let total = 0;
            let playlistRef = null;
            if (useServer) {
                // Cloud playlist — resolve rows to local files (exact link /
                // origin path, one-time heuristic backfill for legacy rows).
                const loaded = await loadCloudPlaylistFiles(accessToken, playlist.id);
                if (!loaded || loaded.total === 0) {
                    toast.warning('This playlist has no tracks');
                    return;
                }
                total = loaded.total;
                filePaths = loaded.filePaths;
                if (filePaths.length === 0) {
                    toast.warning('None of this playlist’s tracks are on this device — these tools work on local audio files');
                    return;
                }
                playlistRef = { source: 'cloud', id: playlist.id, rowIdsByPath: loaded.rowIdsByPath || {} };
            } else {
                const playlistData = await window.electron.db.playlists.get(playlist.id, true);
                if (!playlistData || !playlistData.tracks || playlistData.tracks.length === 0) {
                    toast.warning('This playlist has no tracks');
                    return;
                }
                const tracks = playlistData.tracks.filter(t => t.file || t.filePath);
                if (tracks.length === 0) {
                    toast.warning('No valid file paths found in playlist');
                    return;
                }
                // Filter out cloud/online tracks — they don't have local files to analyze
                total = tracks.length;
                filePaths = tracks
                    .map(t => t.file || t.filePath)
                    .filter(f => !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
                if (filePaths.length === 0) {
                    toast.warning('This playlist only contains cloud tracks — no local files to analyze');
                    return;
                }
                playlistRef = { source: 'local', id: playlist.id };
            }
            setSelectedPlaylistName(playlist.name);
            setSelectedPlaylistRef(playlistRef);
            sourceEpochRef.current += 1;
            dispatch(clearLogs());
            dispatch(setFiles(filePaths));
            const skipped = total - filePaths.length;
            if (skipped > 0) toast.info(`Skipped ${skipped} ${skipped === 1 ? 'track' : 'tracks'} with no local file on this device`);
            track('playlist_opened_in_tool', {
                tab: 'fix-my-tracks',
                source: useServer ? 'cloud' : 'local',
                track_count: filePaths.length,
                skipped_count: skipped,
            });
        } catch (error) {
            console.error('Error loading playlist tracks:', error);
            toast.error('Failed to load playlist tracks');
        } finally {
            setSourceResolving(false);
        }
    };

    rowHandlersRef.current = {
        rowClick: (log, e) => handleRowClick(keyIndex.get(log.filePath), log.filePath, e),
        rowContextMenu: handleRowContextMenu,
        playClick: handlePlayClick,
        pauseClick: handlePauseClick,
        addToQueue: handleAddToQueue,
        removeFromList: removeFilesFromList,
        fixRow: (log) => handleRunYourMagic(true, [log.filePath]),
    };

    // Shared by both designs: the Open menu (same #open-dropdown and
    // .dropdown-item hooks the QA matrix drives) and the playlist picker.
    const sourceLabel = files.length === 0
        ? 'Open'
        : selectedPlaylistName
            ? selectedPlaylistName
            : files.length > 1
                ? `${files.length} files`
                : (files[0].split('/').pop() || files[0]);
    const openMenu = (
        <Dropdown>
            <Dropdown.Toggle
                className={v2 ? 'v2-btn v2-source-chip' : 'button choose-button'}
                id="open-dropdown"
                ref={playlistBtnRef}
                title={v2 ? (files.length === 1 ? files[0] : '') : undefined}
            >
                {v2 ? (
                    <>
                        <DirectoryIcon className="icon" />
                        <span className="v2-source-label">{sourceLabel}</span>
                        {totalAudioFiles > 0 && <span className="v2-faint v2-num">· {totalAudioFiles}</span>}
                    </>
                ) : (
                    <><OpenIcon className="icon" />Open</>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={handleChooseFiles}>Files</Dropdown.Item>
                <Dropdown.Item onClick={handleChooseDir}>Folder</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handlePlaylistDropdownToggle(playlistBtnRef.current)}>Playlist</Dropdown.Item>
                <Dropdown.Item onClick={handleChooseLibrary}>My Library</Dropdown.Item>
                {v2 && files.length > 0 && (
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => handleResetFilePath()}>Clear</Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
    const playlistPicker = playlistDropdownOpen && (
        <div ref={playlistDropdownRef} className="smart-recs-dropdown-menu" style={{
            position: 'fixed',
            top: playlistDropdownPos.top,
            left: playlistDropdownPos.left,
            width: playlistDropdownPos.width,
            zIndex: 1000,
        }}>
            <div className="dropdown-search-wrapper">
                <svg className="dropdown-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                    ref={playlistSearchRef}
                    className="dropdown-search-input"
                    type="text"
                    placeholder="Search playlists..."
                    value={playlistSearch}
                    onChange={(e) => setPlaylistSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                />
            </div>
            <div className="dropdown-options">
                {playlistsLoading ? (
                    <div className="dropdown-skel" aria-label="Loading playlists">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="dropdown-skel-option"><span /></div>)}</div>
                ) : (
                    <>
                        {playlistList
                            .filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase()))
                            .map((pl) => (
                                <button
                                    key={pl.id}
                                    className="dropdown-option"
                                    onClick={() => handlePlaylistSelect(pl)}
                                >
                                    <span className="dropdown-option-name">{pl.name}</span>
                                </button>
                            ))
                        }
                        {playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).length === 0 && (
                            playlistList.length === 0 ? (
                            <div className="dropdown-no-results" style={{ whiteSpace: 'normal', lineHeight: 1.4, textAlign: 'left', padding: '10px 12px' }}>
                                No playlists yet. These tools work on audio files on this computer — import a library (Rekordbox, Serato, Traktor, VirtualDJ) or add local tracks.
                            </div>
                        ) : (
                            <div className="dropdown-no-results">No playlists match your search</div>
                        )
                        )}
                    </>
                )}
            </div>
        </div>
    );

    // ---- DESIGN V2 header, filter chips and per-row actions ----
    const fixDisabled = isProcessing || sourceResolving || files.length === 0 || (hasTrackRows && visibleFixableCount === 0);
    const fixTitle = hasTrackRows && visibleFixableCount === 0
        ? (fixableCount > 0
            ? `Nothing to fix in the current view — ${fixableCount} fixable track${fixableCount === 1 ? ' is' : 's are'} hidden by filters`
            : 'Nothing to fix — no incompatible or convertible files under the current settings')
        : '';
    const v2RowAction = (log) => {
        if (!v2 || !log?.filePath) return null;
        if (log.status === 'incompatible') return fixIncompatibleFiles ? 'Fix' : null;
        if (log.status === 'depends' && /\.flac$/i.test(log.filePath)) return convertAiffToWav ? 'Convert' : null;
        return null;
    };
    const FORMAT_CHIPS = [['wav', 'WAV'], ['mp3', 'MP3'], ['aiff', 'AIFF'], ['flac', 'FLAC'], ['others', 'Others']];
    const formatLabel = activeFileTypes.length === 0 ? 'All' : FORMAT_CHIPS.filter(([k]) => activeFileTypes.includes(k)).map(([, l]) => l).join(', ');
    // A status with no tracks gets no chip (unless it is the active filter).
    const statusChip = (key, label, dot, count, title) => (count > 0 || activeFilters.includes(key)) && (
        <button type="button" key={key} className={`v2-chip sm${activeFilters.includes(key) ? ' on' : ''}`} title={title} onClick={() => dispatch(setActiveFilter(key))}>
            <span className={`v2-dot ${dot}`} />{label}<small>· {count}</small>
        </button>
    );
    const v2Header = v2 && (
        <ToolHeader
            className="v2-tool-compat"
            source={<>{openMenu}{playlistPicker}</>}
            stats={isProcessing ? (
                <ProgressBarWithStop isProcessing={isProcessing} progress={progress} handleStopProcessing={handleStopProcessing} v2 total={logs.filter((l) => l?.filePath).length} />
            ) : files.length === 0 ? (
                null /* the empty page below already says it; repeating it here read as a stray line */
            ) : !hasTrackRows ? (
                <><b>{files.length}</b> {files.length === 1 && !selectedPlaylistName ? 'item' : 'files'} · not analyzed yet</>
            ) : (
                // Same summary line as Loudness: what was checked, what needs work.
                <><b>{totalAudioFiles}</b> analyzed · {fixableCount > 0
                    ? <><b>{fixableCount}</b> to fix</>
                    : 'all compatible'}</>
            )}
            settings={(
                <>
                    <div className="v2-popover-title">Settings</div>
                    <PopoverRow label="Fix incompatible WAV files" hint="Re-writes 32-bit float / odd-header WAVs so players read them"><ToggleSwitch id="v2-fixIncompatibleFiles" checked={!!fixIncompatibleFiles} onChange={(v) => dispatch(setFixIncompatibleFiles(v))} label="" /></PopoverRow>
                    <PopoverRow label="Convert FLAC files" hint={FLAC_DEPENDS_SUMMARY}><ToggleSwitch id="v2-convertAiffToWav" checked={!!convertAiffToWav} onChange={(v) => dispatch(setConvertAiffToWav(v))} label="" /></PopoverRow>
                    <div className={`v2-popover-row${convertAiffToWav ? '' : ' v2-disabled'}`} title={convertAiffToWav ? undefined : 'Turn on "Convert FLAC files" to pick a format'}>
                        <div>Convert FLAC to</div>
                        <div style={{ width: 100 }}>
                            <CustomSelect value={conversionFormat} onChange={(e) => dispatch(setConversionFormat(e.target.value))} className="organizer-select compact-select"
                                options={[{ value: 'wav', label: 'WAV' }, { value: 'mp3', label: 'MP3' }, { value: 'aiff', label: 'AIFF' }]} />
                        </div>
                    </div>
                    <PopoverRow label="Keep original files" hint='On: a "[fixed]" copy next to each original. Off: fix in place.'><ToggleSwitch id="v2-backupOriginalFiles" checked={!!backupOriginalFiles} onChange={(v) => dispatch(setBackupOriginalFiles(v))} label="" /></PopoverRow>
                </>
            )}
            actions={hasTrackRows ? (
                <>
                    <button type="button" className="v2-btn analyze-button" onClick={() => handleRunYourMagic(false)}
                        disabled={isProcessing || sourceResolving || files.length === 0}>
                        Analyze again
                    </button>
                    {/* Nothing fixable at all: the summary says "all compatible", no dead button. */}
                    {(fixableCount > 0 || isProcessing) && (
                    <button type="button" className="v2-btn primary" aria-disabled={fixDisabled ? 'true' : undefined} title={fixTitle}
                        onClick={() => { if (fixDisabled) return; handleRunYourMagic(true); }}>
                        Fix{!isProcessing && visibleFixableCount > 0 ? ` ${visibleFixableCount} ${visibleFixableCount === 1 ? 'track' : 'tracks'}` : ' tracks'}
                    </button>
                    )}
                </>
            ) : files.length > 0 && !isProcessing ? (
                <button type="button" className="v2-btn primary analyze-button" onClick={() => handleRunYourMagic(false)}
                    disabled={isProcessing || sourceResolving || files.length === 0}
                    title={files.length === 0 ? 'Open files, a folder or a playlist first' : ''}>
                    Analyze
                </button>
            ) : null}
        />
    );
    const v2FilterRow = v2 && files.length > 0 && hasTrackRows && (
        <div className="v2-filter-row">
            <button type="button" className={`v2-chip sm${activeFilters.length === 0 ? ' on' : ''}`} onClick={() => dispatch(setActiveFilter('all'))}>All<small>· {totalAudioFiles}</small></button>
            {statusChip('compatible', 'Compatible', 'ok', compatibleFilesFound, 'Plays on every player')}
            {statusChip('incompatible', 'Incompatible', 'bad', incompatibleFilesFound, 'WAV files players reject (32-bit float, odd headers) — Fix re-writes them')}
            {statusChip('depends', 'Depends', 'warn', dependsFilesFound, FLAC_DEPENDS_SUMMARY)}
            {statusChip('fixed', 'Fixed', 'info', fixedFiles, 'Fixed in this session')}
            {convertedFilesFound > 0 && statusChip('converted', 'Converted', 'info', convertedFilesFound, 'Converted in this session')}
            {hiddenFixableCount > 0 && <span className="v2-faint" title="Fix runs on the visible rows only — clear the chips to include these">· {hiddenFixableCount} fixable hidden</span>}
            <span className="v2-tool-spacer" />
            {folderSource ? (
                <span className="v2-seg" role="group" aria-label="List order">
                    <button type="button" className={listGrouping !== 'folder' ? 'on' : ''} title="Flat list, A-Z by track name"
                        onClick={() => { dispatch(setListGrouping('name')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'name' }); }}>Name</button>
                    <button type="button" className={listGrouping === 'folder' ? 'on' : ''} title="Grouped by subfolder, like your own folder structure"
                        onClick={() => { dispatch(setListGrouping('folder')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'folder' }); }}>Folders</button>
                </span>
            ) : selectedPlaylistName ? (
                <span className="v2-seg" role="group" aria-label="List order">
                    <button type="button" className={listSort !== 'name' ? 'on' : ''} title="The source's own order: playlist position or library sort"
                        onClick={() => { dispatch(setListSort('added')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'added' }); }}>Added</button>
                    <button type="button" className={listSort === 'name' ? 'on' : ''} title="A-Z by track name"
                        onClick={() => { dispatch(setListSort('name')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'name' }); }}>Name</button>
                </span>
            ) : null}
            <span className="v2-faint">Format</span>
            <Dropdown autoClose="outside">
                <Dropdown.Toggle className={`v2-chip sm v2-chip-dropdown${activeFileTypes.length ? ' on' : ''}`} id="v2-format-dropdown">{formatLabel}</Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => dispatch(setFileTypeFilter('all'))} active={activeFileTypes.length === 0}>All formats</Dropdown.Item>
                    <Dropdown.Divider />
                    {FORMAT_CHIPS.map(([k, l]) => (
                        <Dropdown.Item key={k} onClick={() => dispatch(setFileTypeFilter(k))} active={activeFileTypes.includes(k)}>{l}</Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );

    return (
        <div
            className={`tab-container ${v2 ? 'v2-tool-page v2-compat-page ' : ''}${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {v2Header}
            {!v2 && <div className="upper-part source-bar-header">
                <div className="source-bar">
                    <div className="source-bar-left">
                        {isProcessing ? (
                            // While running, the title area becomes the progress area
                            // (with its stop button) — no overlap with the actions.
                            <ProgressBarWithStop
                                isProcessing={isProcessing}
                                progress={progress}
                                handleStopProcessing={handleStopProcessing}
                            />
                        ) : (
                            <>
                                <DirectoryIcon className="source-bar-icon" />
                                <span className={`source-bar-title${files.length === 0 ? ' empty' : ''}`} title={files.length === 1 ? files[0] : ''}>
                                    {files.length === 0
                                        ? 'Click "Open" or drop files here'
                                        : selectedPlaylistName
                                            ? selectedPlaylistName
                                            : files.length > 1
                                                ? `${files.length} Files`
                                                : (files[0].split('/').pop() || files[0])}
                                </span>
                                {totalAudioFiles > 0 && <span className="source-bar-meta">· {totalAudioFiles} files</span>}
                                {files.length > 0 && (
                                    <button className="source-bar-clear" onClick={() => handleResetFilePath()} aria-label="Clear" title="Clear">&times;</button>
                                )}
                            </>
                        )}
                    </div>
                    <div className="source-bar-actions">
                        {logs.length > 0 && (
                            <button className="source-bar-icon-btn" onClick={() => handleRefresh()} title="Refresh" aria-label="Refresh"><RefreshIcon className="icon" /></button>
                        )}
                        {openMenu}
                        {playlistPicker}
                        <Button className="button analyze-button main-button" onClick={() => handleRunYourMagic(false)}
                                disabled={isProcessing || sourceResolving || files.length === 0}>
                            <AnalyzeFilesIcon className="icon"/>
                            Analyze
                        </Button>
                        <Button className="button main-button" onClick={() => handleRunYourMagic(true)}
                                disabled={isProcessing || sourceResolving || files.length === 0 || (hasTrackRows && visibleFixableCount === 0)}
                                title={hasTrackRows && visibleFixableCount === 0
                                    ? (fixableCount > 0
                                        ? `Nothing to fix in the current view — ${fixableCount} fixable track${fixableCount === 1 ? ' is' : 's are'} hidden by filters`
                                        : 'Nothing to fix — no incompatible or convertible files under the current settings')
                                    : ''}>
                            <FixFilesIcon className="icon"/>
                            Fix Tracks{!isProcessing && visibleFixableCount > 0 ? ` (${visibleFixableCount})` : ''}
                        </Button>
                    </div>
                </div>
            </div>}
            <div className="lower-part">
                <div className="log-container">
                    <div className="files-container">
                        {files.length === 0 ? (
                        <div className="ftx-empty-state" onClick={handleChooseDir} role="button" tabIndex={0}>
                            <DirectoryIcon className="ftx-empty-icon" />
                            <div className="ftx-empty-title">Drop tracks here to check compatibility</div>
                            <div className="ftx-empty-sub">or click to load files, a folder, or a playlist</div>
                        </div>
                        ) : (<>
                        {v2FilterRow}
                        {!v2 && <div className="file-summary-container">
                            <div className="directory-container log-entry">
                                <DirectoryIcon className = "icon"/>
                                <span>{selectedPlaylistName ? `Playlist: ${selectedPlaylistName} (${files.length} tracks)` : `Directory: ${files.length > 0 ? (files.length > 1 ? `${files.length} Files Selected` : formatPath(files[0])) : 'No directory selected'}`}</span>
                                {hasTrackRows && (folderSource ? (
                                    <span className="list-grouping-toggle" role="group" aria-label="List order">
                                        <button type="button" className={listGrouping !== 'folder' ? 'active' : ''} title="Flat list, A-Z by track name"
                                            onClick={() => { dispatch(setListGrouping('name')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'name' }); }}>Name</button>
                                        <span className="lg-sep">·</span>
                                        <button type="button" className={listGrouping === 'folder' ? 'active' : ''} title="Grouped by subfolder, like your own folder structure"
                                            onClick={() => { dispatch(setListGrouping('folder')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'folder' }); }}>Folders</button>
                                    </span>
                                ) : selectedPlaylistName ? (
                                    <span className="list-grouping-toggle" role="group" aria-label="List order">
                                        <button type="button" className={listSort !== 'name' ? 'active' : ''} title="The source's own order: playlist position or library sort"
                                            onClick={() => { dispatch(setListSort('added')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'added' }); }}>Added</button>
                                        <span className="lg-sep">·</span>
                                        <button type="button" className={listSort === 'name' ? 'active' : ''} title="A-Z by track name"
                                            onClick={() => { dispatch(setListSort('name')); track('list_grouping_changed', { tab: 'fix_my_tracks', grouping: 'name' }); }}>Name</button>
                                    </span>
                                ) : null)}
                            </div>
                            {/* Every count sits in a slot as wide as the run's total (StatCount +
                                utils/statDigits.js) so the row never re-flows while the counters climb */}
                            <div className="changes-container log-entry">
                                <span className="stat-item">
                                    <StatCount className="stat-number" value={totalAudioFiles} digits={countDigits} />
                                    <span className="stat-label">Audio Files</span>
                                </span>
                                <span className="stat-item stat-compatible">
                                    <StatCount className="stat-number" value={compatibleFilesFound} digits={countDigits} />
                                    <span className="stat-label">Compatible</span>
                                </span>
                                <span className="stat-item stat-incompatible">
                                    <StatCount className="stat-number" value={incompatibleFilesFound} digits={countDigits} />
                                    <span className="stat-label">Incompatible</span>
                                    <IncompatibleInfo />
                                </span>
                                <span className="stat-item stat-depends">
                                    <StatCount className="stat-number" value={dependsFilesFound} digits={countDigits} />
                                    <span className="stat-label">Depends</span>
                                    <DependsInfo count={dependsFilesFound} />
                                </span>
                                <span className="stat-item stat-fixed">
                                    <StatCount className="stat-number" value={fixedFiles} digits={countDigits} />
                                    <span className="stat-label">Fixed</span>
                                </span>
                                <span className="stat-item stat-converted">
                                    <StatCount className="stat-number" value={convertedFilesFound} digits={countDigits} />
                                    <span className="stat-label">Converted</span>
                                </span>
                                {hiddenFixableCount > 0 && (
                                    <span className="stat-item stat-filtered-out" title="Fix runs on the visible rows only — clear the filter chips to include these">
                                        <StatCount className="stat-number" value={hiddenFixableCount} digits={countDigits} />
                                        <span className="stat-label">Filtered Out</span>
                                    </span>
                                )}
                            </div>
                        </div>}
                        <div className="file-list-container" ref={listContainerRef}>
                            {logs.length > 0 && logs.every(log => log?.status === 'summary' && log?.msg?.includes('No audio files')) ? (
                                <div className="no-files-message">
                                    <AudioFileIcon className="no-files-icon" />
                                    <span>No audio files were found in folder</span>
                                </div>
                            ) : logs.length > 0 ? (
                                displayRows
                                    .map((log, index) => log?.status === 'folder-header' ? (
                                        <div key={`h${index}`} className="log-entry folder-header">
                                            <DirectoryIcon className="icon" />
                                            <span>{log.folderLabel}</span>
                                        </div>
                                    ) : (() => {
                                        const track = isTrackLog(log);
                                        const audioPath = log.displayPath || log.filePath;
                                        const isCurrent = track && currentlyPlayingPath === audioPath;
                                        return (
                                            <CompatLogRow
                                                key={track ? log.filePath : `i${index}`}
                                                log={log}
                                                enrichedMap={enrichedMap}
                                                selected={track && selectedPaths.has(log.filePath)}
                                                playing={isCurrent && isPlaying}
                                                ctxAt={rowCtx && rowCtx.key === log.filePath ? rowCtx : null}
                                                useStatusBadges={useStatusBadges}
                                                api={rowApi}
                                                v2={v2}
                                                rowAction={v2RowAction(log)}
                                            />
                                        );
                                    })())
                            ) : (
                                <div className="empty-log"></div>
                            )}
                        </div>
                        </>)}
                    </div>
                    {!v2 && <FixSettings/>}
                </div>
            </div>
            {bulkCtx && orderedSelected.length > 1 && (
                <BulkRowContextMenu
                    x={bulkCtx.x}
                    y={bulkCtx.y}
                    onClose={() => setBulkCtx(null)}
                    analyticsFrom="fix_my_tracks"
                    count={orderedSelected.length}
                    items={[
                        {
                            label: `Add ${orderedSelected.length} Tracks to Playlist`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>,
                            onClick: () => setBulkPlaylistTargets(orderedSelected),
                        },
                        { divider: true },
                        {
                            label: `Remove ${orderedSelected.length} Tracks from list`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>,
                            danger: true,
                            onClick: () => removeFilesFromList(orderedSelected, 'menu'),
                        },
                    ]}
                />
            )}
            {bulkPlaylistTargets.length > 0 && (
                <AddToPlaylistModal
                    show={true}
                    onHide={() => setBulkPlaylistTargets([])}
                    bulkFilePaths={bulkPlaylistTargets}
                    initialTab="add"
                    bulkLabel={`${bulkPlaylistTargets.length} tracks`}
                />
            )}
        </div>
    );
};

export default FixMyTracksTab;
